import { chipFor } from "@/lib/status";
import type { EnumGroup, Tone } from "@/lib/api/enums";
import { cn } from "@/lib/utils";

export const TONE: Record<Tone, string> = {
  ok: "bg-ok-bg text-ok border-ok-bd",
  warn: "bg-warn-bg text-warn border-warn-bd",
  danger: "bg-danger-bg text-danger border-danger-bd",
  info: "bg-info-bg text-info border-info-bd",
  neutral: "bg-neutral-bg text-neutral border-neutral-bd",
};
export const DOT: Record<Tone, string> = {
  ok: "bg-ok",
  warn: "bg-warn",
  danger: "bg-danger",
  info: "bg-info",
  neutral: "bg-neutral",
};

/** A dot, a word and a tone. `StatusChip` uses it for backend enums; anything else that needs the
 * same look (a person's active state) uses it directly, so the two never drift apart. */
export function Chip({
  label,
  tone,
  title,
  className,
}: {
  label: string;
  tone: Tone;
  title?: string;
  className?: string;
}) {
  return (
    <span
      title={title}
      data-tone={tone}
      className={cn(
        "inline-flex h-5 items-center gap-1.5 rounded-full border px-2 text-xs font-medium whitespace-nowrap",
        TONE[tone],
        className,
      )}
    >
      <span aria-hidden className={cn("size-1.5 rounded-full", DOT[tone])} />
      {label}
    </span>
  );
}

/** Any backend status as dot + word + tone. Colour is never the only signal: the word is always
 * there, and the raw value stays available on hover for people who need to match the API. */
export function StatusChip({
  group,
  value,
  className,
}: {
  group: EnumGroup;
  value: string;
  className?: string;
}) {
  const { label, tone } = chipFor(group, value);
  return <Chip label={label} tone={tone} title={value} className={className} />;
}
